import { z } from 'zod';
import {
    getIndigentTenantRules,
    type IndigentTenantRules,
} from '../tenant/indigentTenantRules';

export type { IndigentTenantRules };

// Common field validations
export const personalIdSchema = z
    .string()
    .min(1, 'Personal ID is required')
    .max(13, 'Personal ID must not exceed 13 digits');

/** Monthly income / salary: positive amount, optional cents (up to 2 decimal places). */
export const incomeSchema = z
    .string()
    .min(1, 'Income is required')
    .regex(
        /^\d+(\.\d{0,2})?$/,
        'Enter a valid amount (e.g. 100 or 100.50)',
    );

export const isCellNumberRequired = (): boolean => getIndigentTenantRules().cellNumberRequired;

function buildCellNumberSchema(rules: IndigentTenantRules) {
    const baseSchema = z
        .string()
        .regex(
            /^(?:0[6-8]\d{8}|(?:\+27|0027)[6-8]\d{8})$/,
            'Please enter a valid cell number (e.g., 0821234567, +27821234567 or 0027821234567)',
        );

    const optionalSchema = baseSchema.optional().or(z.string().length(0));

    if (!rules.cellNumberRequired) {
        return optionalSchema;
    }

    return optionalSchema.refine(
        val => val !== undefined && val !== null && val.length > 0,
        { message: 'Cell number is required' },
    );
}

function buildEmailSchema(rules: IndigentTenantRules) {
    if (rules.emailRequired) {
        return z
            .string()
            .min(1, 'Email address is required')
            .email('Enter a valid email address');
    }
    return z.union([z.literal(''), z.string().email('Enter a valid email address')]);
}

const electricityMeterSchemaOptional = z.union([
    z.literal(''),
    z.string().regex(/^\d{3,20}$/, 'Electricity meter number must be 3-20 digits'),
]);

const electricityMeterSchemaRequired = z
    .string()
    .min(1, 'Electricity meter number is required')
    .regex(/^\d{3,20}$/, 'Electricity meter number must be 3-20 digits');

function buildElectricityMeterSchema(rules: IndigentTenantRules) {
    return rules.electricityMeterRequired
        ? electricityMeterSchemaRequired
        : electricityMeterSchemaOptional;
}

const accountNumberSchemaOptional = z.union([
    z.literal(''),
    z.string().regex(/^\d{3,20}$/, 'Account number must be 3-20 digits'),
]);

const accountNumberSchemaRequired = z
    .string()
    .min(1, 'Account Number is required')
    .regex(/^\d{3,20}$/, 'Account number must be 3-20 digits');

function buildAccountNumberSchema(rules: IndigentTenantRules) {
    return rules.accountNumberRequired
        ? accountNumberSchemaRequired
        : accountNumberSchemaOptional;
}

function buildErfNoSchema(rules: IndigentTenantRules) {
    const base = z.string().min(1, 'Erf No is required');
    const maxDigits = rules.erfNoMaxDigits;
    if (maxDigits == null) {
        return base;
    }
    return base.regex(
        new RegExp(`^\\d{1,${maxDigits}}$`),
        `Erf number must not exceed ${maxDigits} digits`,
    );
}

export const incomeSourceSchema = z
    .string()
    .optional()
    .or(z.string().length(0));

export function createPersonalInfoSchema(rules: IndigentTenantRules) {
    const cellSchema = buildCellNumberSchema(rules);
    const emailSchema = buildEmailSchema(rules);

    return z.object({
        financialYear: z.string().min(1, 'Financial year is required'),
        name: z.string().min(1, 'Name is required'),
        surname: z.string().min(1, 'Surname is required'),
        personalId: personalIdSchema,
        employerName: z.string().optional(),
        income: incomeSchema,
        incomeSource: incomeSourceSchema,
        cellNumber: cellSchema,
        email: emailSchema,
        services: z.array(z.string()).min(1, 'At least one service must be selected'),
    });
}

export type PersonalInfoInputs = z.infer<ReturnType<typeof createPersonalInfoSchema>>;

export function createAddressServicesSchema(rules: IndigentTenantRules) {
    const cellSchema = buildCellNumberSchema(rules);
    const electricityMeterSchema = buildElectricityMeterSchema(rules);
    const accountNumberSchema = buildAccountNumberSchema(rules);
    const erfNoSchema = buildErfNoSchema(rules);

    return z.object({
        ward: z.string().min(1, 'Ward is required'),
        town: z.string().min(1, 'Town is required'),
        erfNo: erfNoSchema,
        address1: z.string().min(1, 'Address line 1 is required'),
        address2: z.string().optional(),
        address3: z.string().optional(),
        accountNumber: accountNumberSchema,
        electricityMeterNo: electricityMeterSchema,
        occupants: z
            .array(
                z.object({
                    name: z.string().min(1, 'Name is required'),
                    surname: z.string().min(1, 'Surname is required'),
                    personalId: personalIdSchema,
                    employerName: z.string().optional(),
                    monthlyIncome: incomeSchema,
                    cellNumber: cellSchema,
                    photoPath: z.string().optional(),
                }),
            )
            .optional(),
        occupantPhotos: z.array(z.any()).optional(),
    });
}

export type AddressServicesInputs = z.infer<ReturnType<typeof createAddressServicesSchema>>;

export interface FormOccupant {
    name: string;
    surname: string;
    personalId: string;
    employerName?: string;
    monthlyIncome: string;
    cellNumber?: string | undefined;
    photoPath?: string;
}
